import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff, ErrorOutline } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { logo } from "../../assets";
import { reset_password_api, verify_reset_token_api } from "../../DAL/Auth/PasswordReset";
import {
  authKeyframes,
  authInputSx,
  authPageSx,
  authCardSx,
  authButtonStyle,
} from "./authStyles";

const MIN_PASSWORD_LENGTH = 8; // matches the backend floor in reset_password_admin.js

export default function ResetPassword() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      if (!token) {
        setIsValidToken(false);
        setIsVerifying(false);
        return;
      }

      try {
        const result = await verify_reset_token_api(token);
        if (cancelled) return;
        if (result?.valid) {
          setAccountEmail(result.admin?.email || "");
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
        }
      } catch (error) {
        if (!cancelled) setIsValidToken(false);
      } finally {
        if (!cancelled) setIsVerifying(false);
      }
    };

    verify();
    return () => { cancelled = true; };
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password.length < MIN_PASSWORD_LENGTH) {
      enqueueSnackbar(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`, { variant: "error" });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      enqueueSnackbar("Passwords do not match", { variant: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await reset_password_api({ token, password: formData.password });

      if (result?.code === 200) {
        // Deliberately not auto-signed-in: the reset endpoint issues no token,
        // and making them type the new password once proves it landed.
        enqueueSnackbar("Password reset. You can sign in now.", { variant: "success" });
        navigate("/login");
      } else {
        enqueueSnackbar(result?.message || "Could not reset password", { variant: "error" });
        setIsSubmitting(false);
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Could not reset password", { variant: "error" });
      setIsSubmitting(false);
    }
  };

  const heading = {
    fontSize: "22px", fontWeight: 800, color: "#fff", fontFamily: "'Rubik', sans-serif",
    mb: 0.5, textAlign: "center", letterSpacing: "-0.4px",
  };
  const sub = {
    fontSize: "13px", color: "rgba(255,255,255,0.5)", fontFamily: "'Rubik', sans-serif",
    mb: 3.5, textAlign: "center", lineHeight: 1.6,
  };

  return (
    <Box sx={authPageSx}>
      <style>{authKeyframes}</style>

      <Box sx={{
        position: "absolute", top: "8%", left: "8%", width: 340, height: 340, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(192,30,46,0.45), transparent 70%)",
        filter: "blur(10px)", animation: "floatOrb1 12s ease-in-out infinite", pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", bottom: "5%", right: "10%", width: 420, height: 420, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(30,58,95,0.55), transparent 70%)",
        filter: "blur(10px)", animation: "floatOrb2 14s ease-in-out infinite", pointerEvents: "none",
      }} />

      <Box sx={authCardSx}>
        <Box sx={{ mb: 3.5, textAlign: "center" }}>
          <img
            src={logo}
            alt="XAir"
            style={{ height: 32, width: "auto", animation: "logoGlow 3s ease-in-out infinite" }}
          />
        </Box>

        {isVerifying ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={26} sx={{ color: "#c01e2e" }} />
            <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.5)", fontFamily: "'Rubik', sans-serif" }}>
              Checking your link…
            </Typography>
          </Box>
        ) : !isValidToken ? (
          <>
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <ErrorOutline sx={{ fontSize: 44, color: "#ff6b7a" }} />
            </Box>
            <Typography sx={heading}>Link expired</Typography>
            <Typography sx={sub}>
              Reset links last an hour and work once. Request a fresh one and it'll be waiting in your inbox.
            </Typography>
            <RouterLink to="/forgot-password" style={{ textDecoration: "none" }}>
              <button type="button" style={authButtonStyle(false)}>Request a new link</button>
            </RouterLink>
          </>
        ) : (
          <>
            <Typography sx={heading}>Set a new password</Typography>
            <Typography sx={sub}>
              {accountEmail
                ? <>You're resetting the password for <strong style={{ color: "rgba(255,255,255,0.8)" }}>{accountEmail}</strong>.</>
                : "Choose a new password for your account."}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="New password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoFocus
                  fullWidth
                  size="small"
                  sx={authInputSx}
                  helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  FormHelperTextProps={{
                    sx: { color: "rgba(255,255,255,0.35)", fontSize: "11px", fontFamily: "'Rubik', sans-serif", ml: 0.5 },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: "rgba(255,255,255,0.4)" }}
                        >
                          {showPassword
                            ? <VisibilityOff sx={{ fontSize: 16 }} />
                            : <Visibility sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm new password"
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  size="small"
                  sx={authInputSx}
                />

                <button type="submit" disabled={isSubmitting} style={authButtonStyle(isSubmitting)}>
                  {isSubmitting
                    ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                    : "Reset password"}
                </button>
              </Box>
            </form>
          </>
        )}

        <Typography sx={{
          textAlign: "center", color: "rgba(255,255,255,0.45)", fontSize: "12px",
          fontFamily: "'Rubik', sans-serif", mt: 3,
        }}>
          <RouterLink to="/login" style={{ color: "#ff6b7a", fontWeight: 600, textDecoration: "none" }}>
            Back to sign in
          </RouterLink>
        </Typography>
      </Box>
    </Box>
  );
}
